import { z } from 'zod';
/* ==========================================================================
 *  GDPR data-lifecycle contract (ISSUE-050 export / ISSUE-051 erasure)
 *
 *  Two rights, one shared invariant: there must be a SINGLE declarative source
 *  of truth for "what personal data exists and how each category is handled,"
 *  so export (Art. 20) and erasure (Art. 17) can never drift apart. That source
 *  is `PERSONAL_DATA_REGISTRY` below: every user-owned table appears exactly
 *  once with its export + erasure policy. A new table added later MUST get a
 *  registry entry (enforced by a server-side coverage test, see ADR-0006), or
 *  it silently escapes both export and deletion — the exact compliance failure
 *  this contract exists to prevent.
 *
 *  CONTRACT TYPES + the registry only — zod is the single source of truth; the
 *  Settings UI (J7) renders these and the server jobs execute against them. The
 *  registry lists DB table NAMES as strings (no `@nutians/db` import — shared is
 *  browser-safe); the server maps names → Drizzle tables and a coverage test
 *  asserts the mapping is exhaustive.
 * ========================================================================== */
/* ----- Personal-data registry (the single source of truth) --------------- */
/** Every category of user data the system holds. Used by BOTH export and
 *  erasure so the two views of "personal data" are identical by construction. */
export const dataCategorySchema = z.enum([
    'account_identity', // user row: email, phone (ADR-0018), name, locale, attestation
    'profile',
    'targets',
    'raw_logs',
    'meal_entries',
    'body_metrics',
    'summaries',
    'food_overrides', // user_food_override + user_mention_override
    'chat', // chat_session + chat_message
    'content_keys', // wrapped per-user DEKs for encrypted free text and cycle data
    'cycle', // cycle_log — opt-in, encrypted special-category menstrual-cycle data
    'chat_battery', // message_battery — regenerating chat battery (ADR-0024 / T10.1)
    'image_quota', // image_quota_daily — vision daily call counter (ADR-0014 / T6.1)
    'ai_usage', // anonymized on erasure (userId → null), see below
    'ai_cost_daily', // nightly rollup of ai_usage; cascade-deleted on erasure (ADR-0011 §6)
    'auth', // session + account + verification (by email)
    'export_jobs', // export_job — job metadata (not the ZIP content)
    'report_jobs', // report_job — premium PDF report job metadata (ISSUE-052)
    'push_tokens', // Expo push tokens (operational delivery infra)
    'web_push_subscriptions', // W3C Push API subscriptions
    'notification_logs', // Alert dispatch log
    'notification_preferences', // User alert mute preferences
    'consent_events', // Consent audit log (ADR-0021)
    'subscription', // RevenueCat / Stripe entitlement (ADR-0010)
    'admin_access_audit', // ADR-0011 admin access log
    'manual_overrides', // manual_metric_override (ADR-0013 §7)
    'auth_events', // auth_event audit log (T2.1 / ISSUE-062)
    'milestone_awards', // milestone_award behavior firsts (ISSUE-067 / ADR-0020 §3)
    'activation_events', // app_event funnel log (T2.8 / ISSUE-200 / ISSUE-201)
    'referral_codes', // referral_code — one invite code per user (ADR-0024 §4 / T10.3)
    'referrals', // referral — attribution links referrer↔referred (ADR-0024 §4 / T10.3)
    'saved_meals', // saved_meal + saved_meal_item — named meal bundles for 1-tap re-logging (T11.1)
    'health_activity', // health_activity_day — imported daily steps, CONTEXT-only (ADR-0027 / T14.2)
    'health_links', // health_link — Apple Health / Health Connect connection + sync state (ADR-0027 / T14.2)
    'expenditure_estimates', // expenditure_estimate — adaptive coaching weekly estimates (T12.5 / ADR-0026)
    'plan_templates', // plan_template + plan_template_item — reusable weekly meal scaffolds (ADR-0029 / T16.2)
    'meal_plans', // meal_plan + meal_plan_item — dated day plans + build-rest-of-today (ADR-0029 / T16.2)
    'coach_links', // coach_link — read-only week-summary sharing with a coach/partner (T16.5 / ISSUE-081)
]);
/** How a category is treated on account erasure (Art. 17):
 *  - `cascade`  → removed by the DB cascade when the `user` row is deleted.
 *  - `set_null` → row kept but de-identified (userId nulled). For data with a
 *                 legitimate retention basis once anonymized (e.g. `ai_usage`
 *                 cost accounting, D-16). MUST contain no other PII.
 *  - `by_email` → no user FK; purged explicitly by the user's email/identifier
 *                 (e.g. `verification` tokens).
 *  - `shred`    → key material explicitly deleted before the user row cascade.
 *  - `retained` → deliberately survives erasure under a legal basis (e.g. the
 *                 deletion-audit proof, 7y). NEVER contains profile content. */
export const erasureModeSchema = z.enum(['cascade', 'set_null', 'by_email', 'shred', 'retained']);
export const dataCategoryDescriptorSchema = z.object({
    category: dataCategorySchema,
    /** DB table names this category covers (mapped to Drizzle tables server-side). */
    tables: z.array(z.string()).min(1),
    /** Included in a GDPR export (Art. 20)? Auth tokens/quota are operational, not exported. */
    exported: z.boolean(),
    erasureMode: erasureModeSchema,
    /** Human note for the privacy policy / audit. */
    note: z.string(),
});
/**
 * THE registry. Export iterates `exported === true`; erasure iterates every row
 * and applies `erasureMode`. Server coverage test: every user-owned Drizzle
 * table must be named here exactly once (catches new tables at CI time).
 */
export const PERSONAL_DATA_REGISTRY = [
    {
        category: 'account_identity',
        tables: ['user'],
        exported: true,
        erasureMode: 'cascade',
        note: "The user identity row: email, phone number + verified flag (ADR-0018), name, locale, 18+ attestation, account status. Exported for Art. 20. cascade: the user row is the erasure root — runPurgeJob deletes it directly (every other 'cascade' category hangs off its FK).",
    },
    {
        category: 'profile',
        tables: ['profile'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Demographics + dietary context; 1:1 with user.',
    },
    {
        category: 'targets',
        tables: ['target'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Versioned macro targets.',
    },
    {
        category: 'raw_logs',
        tables: ['raw_log'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Original log text + extraction metadata; parent of entries.',
    },
    {
        category: 'meal_entries',
        tables: ['meal_entry'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Structured food ledger (also cascades via raw_log).',
    },
    {
        category: 'body_metrics',
        tables: ['body_metric'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Weight/measurements.',
    },
    {
        category: 'summaries',
        tables: ['summary'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Daily/weekly/monthly summary documents.',
    },
    {
        category: 'food_overrides',
        tables: ['user_food_override', 'user_mention_override'],
        exported: true,
        erasureMode: 'cascade',
        note: 'User correction-learning layer.',
    },
    {
        category: 'chat',
        tables: ['chat_session', 'chat_message'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Assistant transcripts (audit, not data-of-record; messages cascade via session).',
    },
    {
        category: 'content_keys',
        tables: ['user_content_key', 'user_cycle_key'],
        exported: false,
        erasureMode: 'shred',
        note: 'Wrapped per-user DEKs for encrypted raw logs/chat messages and cycle data (ADR-0025 / ADR-0030). Key material is not exported; erasure explicitly deletes it and evicts in-memory DEK caches.',
    },
    {
        category: 'cycle',
        tables: ['cycle_log'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Opt-in menstrual-cycle logs (ADR-0030). Event dates and symptoms are encrypted at rest with cycle-scoped key material; exported decrypted for Art. 20 and cascade-deleted on user erasure. Consent withdrawal deletes rows and crypto-shreds the cycle key.',
    },
    {
        category: 'chat_battery',
        tables: ['message_battery'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Regenerating chat message battery (ADR-0024 / T10.1). Holds regen/earned credit balances and last-check-in date. Operational state; not exported. Cascade-deleted on user erasure.',
    },
    {
        category: 'ai_usage',
        tables: ['ai_usage'],
        exported: false,
        erasureMode: 'set_null',
        note: 'Token/cost rows. userId nulled on erasure; retained anonymized for cost accounting (D-16). Contains no PII beyond userId.',
    },
    {
        category: 'ai_cost_daily',
        tables: ['ai_cost_daily'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Nightly rollup of ai_usage for admin cost views (ADR-0011 §4.2). Operational aggregate; not exported. Cascade-deleted on user erasure.',
    },
    {
        category: 'auth',
        tables: ['session', 'account', 'verification'],
        exported: false,
        erasureMode: 'by_email',
        note: 'session/account cascade via user FK; verification has no FK and is purged by email/identifier.',
    },
    {
        category: 'export_jobs',
        tables: ['export_job'],
        exported: false,
        erasureMode: 'cascade',
        note: 'GDPR data-export job records; not exported to the user (they receive the ZIP, not job metadata).',
    },
    {
        category: 'report_jobs',
        tables: ['report_job'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Premium PDF report job records (ISSUE-052); not exported to the user (they receive the PDF, not job metadata). Cascade-deleted on erasure.',
    },
    {
        category: 'push_tokens',
        tables: ['push_token'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Expo push tokens; operational delivery infra. Cascade-deleted on erasure.',
    },
    {
        category: 'web_push_subscriptions',
        tables: ['web_push_subscription'],
        exported: false,
        erasureMode: 'cascade',
        note: 'W3C Push API subscriptions; operational delivery infra. Cascade-deleted on erasure.',
    },
    {
        category: 'notification_logs',
        tables: ['notification_log'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Alert dispatch log (Art. 20 transparency). Exported so users can see alerts sent.',
    },
    {
        category: 'notification_preferences',
        tables: ['notification_preference'],
        exported: true,
        erasureMode: 'cascade',
        note: 'User mute preferences for alerts.',
    },
    {
        category: 'consent_events',
        tables: ['consent_event'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Append-only consent audit log (ADR-0021). User-linked events cascade on erasure. Anonymous landing analytics events have no account link and are outside account erasure.',
    },
    {
        category: 'subscription',
        tables: ['subscription', 'subscription_event'],
        exported: true,
        erasureMode: 'cascade',
        note: 'IAP / Stripe entitlement state and webhook audit log (ADR-0010). cascade: subscription row is the derived cache; financial record of record lives at Apple/Google/RC.',
    },
    {
        category: 'admin_access_audit',
        tables: ['admin_access_audit'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Internal admin request log (ADR-0011). Cascade-deleted when the admin user is erased.',
    },
    {
        category: 'manual_overrides',
        tables: ['manual_metric_override'],
        exported: true,
        erasureMode: 'cascade',
        note: 'User manual metric overrides (set_metric commands — authoritative day totals, ADR-0013 §7). Cascade-deleted on erasure.',
    },
    {
        category: 'auth_events',
        tables: ['auth_event'],
        exported: true,
        erasureMode: 'set_null',
        note: 'Sign-in / OTP audit log (T2.1 / ISSUE-062). user_id is SET NULL on user erasure (rows are anonymised, not deleted). No plaintext phone or OTP — only HMAC-SHA256 of the phone. Exported for Art. 20 transparency. Pruned after 90 days by AUTH_EVENT_RETENTION cron.',
    },
    {
        category: 'milestone_awards',
        tables: ['milestone_award'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Behavior-only milestone unlocks (ISSUE-067 / ADR-0020 §3). Records when a user first logged, first exported, etc. Cascade-deleted on user erasure. Exported for Art. 20 transparency.',
    },
    {
        category: 'image_quota',
        tables: ['image_quota_daily'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Operational daily vision-call counters (ADR-0014 §4 / T6.1). Mirrors chat_quota_daily. Not exported; cascade-deleted on user erasure.',
    },
    {
        category: 'activation_events',
        tables: ['app_event'],
        exported: false,
        erasureMode: 'cascade',
        // Server-side product events are legitimate-interest operational metrics (not consent-gated).
        // Client-emitted events (onboarding steps, paywall views) are gated on analytics consent (T2.4).
        // No PII in props — only step names, booleans, counts. Cascade-deleted on user erasure.
        // Pruned after 13 months by APP_EVENT_RETENTION cron. Not exported (aggregate KPI data only).
        note: 'Activation-funnel event log (T2.8 / ISSUE-200 / ISSUE-201). No PII in props. Cascade-deleted on user erasure. Pruned after 13 months. Not exported (operational KPI metrics).',
    },
    {
        category: 'referral_codes',
        tables: ['referral_code'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Referral invite codes issued per user (ADR-0024 §4 / T10.3). One code per referrer, generated lazily on first GET /me/referral. Cascade-deleted on user erasure (without the referrer the code is meaningless).',
    },
    {
        category: 'referrals',
        tables: ['referral'],
        exported: false,
        erasureMode: 'set_null',
        note: 'Referral attribution links referrer↔referred (ADR-0024 §4 / T10.3). referrer_id and referred_id are SET NULL on erasure of either party — the row and its credited flag survive for anti-abuse (prevents delete-and-re-attribute loops). No exported personal content beyond the anonymized relationship.',
    },
    {
        category: 'saved_meals',
        tables: ['saved_meal', 'saved_meal_item'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Named meal bundles for 1-tap re-logging (T11.1 / ISSUE-070). saved_meal_item cascades via saved_meal FK. Exported for Art. 20 (user-defined meal data). Cascade-deleted on user erasure.',
    },
    {
        category: 'health_activity',
        tables: ['health_activity_day'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Imported daily step counts from Apple Health / Health Connect (ADR-0027 / T14.2 / ISSUE-075). Special-category health data; CONTEXT ONLY — never an expenditure input (ADR-0027 §2). Exported for Art. 20 transparency. Cascade-deleted on user erasure.',
    },
    {
        category: 'health_links',
        tables: ['health_link'],
        exported: false,
        erasureMode: 'cascade',
        note: 'Per-source Apple Health / Health Connect connection + sync state (ADR-0027 / T14.2 / ISSUE-075). Operational connection metadata (scopes, status, last-sync cursor); not exported (precedent: push_tokens). Disconnect stops reads (consent withdrawal). Cascade-deleted on user erasure.',
    },
    {
        category: 'expenditure_estimates',
        tables: ['expenditure_estimate'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Append-only adaptive-coaching weekly expenditure estimates (T12.5 / ADR-0026 / ISSUE-072). Holds energy-balance window data; no raw log text. Exported for Art. 20 transparency. Cascade-deleted on user erasure.',
    },
    {
        category: 'plan_templates',
        tables: ['plan_template', 'plan_template_item'],
        exported: true,
        erasureMode: 'cascade',
        note: 'User-created reusable weekly meal plan templates (ADR-0029 / T16.2 / ISSUE-077). plan_template_item cascades via plan_template FK. Items reference saved_meal/recipe by FK (no macro duplication). Exported for Art. 20 (user-authored planning data). Cascade-deleted on user erasure.',
    },
    {
        category: 'meal_plans',
        tables: ['meal_plan', 'meal_plan_item'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Dated day plans and their items (ADR-0029 / T16.2 / ISSUE-077). Sources: template apply, manual edit, or build-rest-of-today. meal_plan_item cascades via meal_plan FK. logged_entry_id links to raw_log (SET NULL if log deleted). Exported for Art. 20. Cascade-deleted on user erasure.',
    },
    {
        category: 'coach_links',
        tables: ['coach_link'],
        exported: true,
        erasureMode: 'cascade',
        note: 'Read-only week-summary sharing grants issued by this user to a coach/partner (T16.5 / ISSUE-081). Records grantee email, scope, and status. Chat content and raw logs are NEVER included in the shared view (privacy by default). Cascade-deleted on owner erasure; accepted links also cascade-delete on grantee erasure so grantee email does not linger in an active account link.',
    },
];
/* ----- Export (Art. 20, ISSUE-050) --------------------------------------- */
export const exportScopeSchema = z.enum(['all', 'range']);
/** Body for `POST /me/exports`. Range is inclusive; omitted → all history. */
export const createExportSchema = z
    .object({
    scope: exportScopeSchema.default('all'),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
})
    .refine((v) => v.scope === 'all' || (v.from !== undefined && v.to !== undefined), {
    message: 'scope "range" requires both from and to.',
});
export const exportJobStatusSchema = z.enum([
    'pending', // enqueued
    'processing', // assembling ZIP
    'ready', // uploaded, link live
    'expired', // link past its 7-day TTL
    'failed',
]);
/** A manifest entry written into the ZIP so the user sees what they got. */
export const exportManifestEntrySchema = z.object({
    category: dataCategorySchema,
    rowCount: z.number().int().nonnegative(),
    files: z.array(z.string()).default([]), // e.g. ["meal_entries.csv", "meal_entries.json"]
});
export const exportJobSchema = z.object({
    id: z.string().uuid(),
    status: exportJobStatusSchema,
    scope: exportScopeSchema,
    requestedAt: z.string().datetime(),
    /** Signed URL; present only when `status === 'ready'`. Never logged. */
    downloadUrl: z.string().url().nullable().default(null),
    /** Link TTL — 7 days from ready (ISSUE-050 AC). */
    expiresAt: z.string().datetime().nullable().default(null),
    manifest: z.array(exportManifestEntrySchema).default([]),
});
/* ----- Erasure (Art. 17, ISSUE-051) -------------------------------------- */
/** Account lifecycle. `active` is normal; the rest are the deletion flow.
 *  Login is disabled from `confirmed` onward (ISSUE-051 AC). */
export const accountStatusSchema = z.enum([
    'active',
    'deletion_requested', // user asked; awaiting email-link confirmation
    'deletion_confirmed', // confirmed; login disabled; purge scheduled (≤30d)
    'deleted', // purge completed
]);
/** Result of `GET /me/account` (drives the Settings deletion UI). */
export const accountStateSchema = z.object({
    status: accountStatusSchema,
    /** When the scheduled hard-purge will run (set once confirmed). */
    purgeScheduledFor: z.string().datetime().nullable().default(null),
});
/** Body for `POST /me/account/deletion/confirm` — the token from the email link
 *  (two-step + email confirm, ISSUE-051 AC). */
export const confirmErasureSchema = z.object({
    token: z.string().min(1),
});
/**
 * The deletion-audit record (ISSUE-051 AC: retained 7 years for compliance
 * proof). DELIBERATELY survives erasure — so it must hold NO profile content,
 * only the fact of the request + a non-reversible user reference. This shape is
 * the `retained` exception in the registry; the server stores it in a table with
 * NO cascading FK to `user`.
 */
export const erasureAuditSchema = z.object({
    /** Opaque, retained reference to the erased account (not an FK; e.g. a hash). */
    subjectRef: z.string(),
    requestedAt: z.string().datetime(),
    confirmedAt: z.string().datetime().nullable().default(null),
    completedAt: z.string().datetime().nullable().default(null),
    /** Which registry categories were purged, for the proof. */
    categoriesPurged: z.array(dataCategorySchema).default([]),
});
//# sourceMappingURL=gdpr.js.map